import { Injectable } from '@angular/core';
import { ofType, createEffect, Actions } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap } from 'rxjs';

import { SystemManagerService } from '@cpd-app/core-module/services';

import {
  feedback,
  getIssues,
  getIssuesFailure,
  getIssuesSuccess,
  feedbackSuccess,
  feedbackFailure,
  reportBug,
  reportBugSuccess,
  reportBugFailure,
  getSystemReports,
  getSystemReportsSuccess,
  getSystemReportsFailure,
  createSystemStatusReport,
  createSystemStatusReportSuccess,
  createSystemStatusReportFailure,
  togglePublishAllSystemStatusReport,
  togglePublishAllSystemStatusReportSuccess,
  togglePublishAllSystemStatusReportFailure,
  deleteSystemStatusReport,
  deleteSystemStatusReportSuccess,
  deleteSystemStatusReportFailure,
  markBugReportsViewed,
  markBugReportsViewedSuccess,
  markBugReportsViewedFailure,
  getFeedbacks,
  getFeedbacksSuccess,
  getFeedbacksFailure,
  getFeedbackAnalytics,
  getFeedbackAnalyticsSuccess,
  getFeedbackAnalyticsFailure
} from './system-status.actions';

@Injectable()
export class SystemStatusEffects {
  constructor(
    private actions$: Actions,
    private systemManagerService: SystemManagerService
  ) {}

  public getIssues$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getIssues),
      switchMap(() => {
        return this.systemManagerService.getBugReports().pipe(
          map((issues) =>
            getIssuesSuccess({
              issues
            })
          ),
          catchError((error) => of(getIssuesFailure({ error })))
        );
      })
    );
  });

  public getSystemReports$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getSystemReports),
      switchMap(() => {
        return this.systemManagerService.getSystemStatusReports().pipe(
          map((systemReports) =>
            getSystemReportsSuccess({
              systemReports
            })
          ),
          catchError((error) => of(getSystemReportsFailure({ error })))
        );
      })
    );
  });

  public feedback$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(feedback),
      switchMap(({ input }) => {
        return this.systemManagerService.feedback(input).pipe(
          map(() => {
            return feedbackSuccess();
          }),
          catchError((error) => of(feedbackFailure({ error })))
        );
      })
    );
  });

  public reportBug$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reportBug),
      switchMap(({ input }) => {
        return this.systemManagerService.reportBug(input).pipe(
          map((issue) => {
            return reportBugSuccess({ issue });
          }),
          catchError((error) => of(reportBugFailure({ error })))
        );
      })
    );
  });

  public createSystemStatusReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createSystemStatusReport),
      switchMap(({ input }) => {
        return this.systemManagerService.createSystemStatusReport(input).pipe(
          mergeMap((report) => [
            getSystemReports(),
            createSystemStatusReportSuccess({ report })
          ]),
          catchError((error) => of(createSystemStatusReportFailure({ error })))
        );
      })
    );
  });

  public togglePublishAllSystemStatusReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(togglePublishAllSystemStatusReport),
      switchMap(({ input }) => {
        return this.systemManagerService
          .togglePublishAllSystemStatusReport(input)
          .pipe(
            mergeMap((reports) => [
              togglePublishAllSystemStatusReportSuccess({ reports }),
              getSystemReports()
            ]),
            catchError((error) =>
              of(togglePublishAllSystemStatusReportFailure({ error }))
            )
          );
      })
    );
  });

  public deleteSystemStatusReport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteSystemStatusReport),
      switchMap(({ ids }) => {
        return this.systemManagerService
          .deletePublishedSystemStatusReport(ids)
          .pipe(
            mergeMap((success) => [
              deleteSystemStatusReportSuccess({ success }),
              getSystemReports()
            ]),
            catchError((error) =>
              of(deleteSystemStatusReportFailure({ error }))
            )
          );
      })
    );
  });

  public markBugReportsViewed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(markBugReportsViewed),
      switchMap(({ input }) => {
        return this.systemManagerService.markBugReportsViewed(input).pipe(
          map((issues) => markBugReportsViewedSuccess({ issues })),
          catchError((error) => of(markBugReportsViewedFailure({ error })))
        );
      })
    );
  });

  public getFeedbacks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getFeedbacks),
      switchMap(() => {
        return this.systemManagerService.getFeedbacks().pipe(
          map((feedbacks) =>
            getFeedbacksSuccess({
              feedbacks
            })
          ),
          catchError((error) => of(getFeedbacksFailure({ error })))
        );
      })
    );
  });

  public getFeedbackAnalytics$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(getFeedbackAnalytics),
      switchMap(() => {
        return this.systemManagerService.getFeedbackAnalytics().pipe(
          map((analytics) =>
            getFeedbackAnalyticsSuccess({
              analytics
            })
          ),
          catchError((error) => of(getFeedbackAnalyticsFailure({ error })))
        );
      })
    );
  });
}
